// 浏览器端共享工具：DOM 构建辅助、清单加载、媒体 URL、Ruffle 按需加载。
import type { Manifest, StoryInfo } from "../shared/manifest";
import { PATHS } from "../shared/navigation";

const MANIFEST_URL = "/api/manifest";

/* ---------- Ruffle 最小类型 ---------- */
export interface RufflePlayerHandle {
  load(url: string): Promise<unknown>;
  enterFullscreen?(): void;
  exitFullscreen?(): void;
}
export interface RufflePlayerElement extends HTMLElement {
  ruffle(): RufflePlayerHandle;
  /** 主音量 0–1(Ruffle 播放器元素原生支持,页面级控制,无需改 SWF) */
  volume: number;
}
export interface RufflePlayerOptions {
  autoplay?: "on" | "off" | "auto";
  unmuteOverlay?: "visible" | "hidden";
  letterbox?: "on" | "off";
  openUrlMode?: "allow" | "deny" | "confirm";
  scale?: "showAll" | "noBorder" | "exactFit" | "noScale";
}
export interface RuffleInstance {
  createPlayer(options?: RufflePlayerOptions): RufflePlayerElement;
}

declare global {
  interface Window {
    RufflePlayer?: { newest(): RuffleInstance };
    __manifest?: Manifest;
    __currentStory?: StoryInfo;
    __currentPlayer?: RufflePlayerElement;
    __currentSrc?: string;
  }
}

/* ---------- 工具 ---------- */
export function mediaUrl(rel: string): string {
  const encoded = rel
    .split("/")
    .filter(Boolean)
    .map((seg) => encodeURIComponent(seg))
    .join("/");
  return "/media/" + encoded;
}

export function el(tag: string, cls?: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text != null) node.textContent = text;
  return node;
}

/** 站内链接：统一带 data-route，由路由器拦截做 pushState 切换。 */
export function routeLink(href: string, cls?: string): HTMLAnchorElement {
  const a = document.createElement("a");
  a.href = href;
  a.dataset.route = "";
  if (cls) a.className = cls;
  return a;
}

export async function loadManifest(): Promise<Manifest> {
  const res = await fetch(MANIFEST_URL, { headers: { accept: "application/json" } });
  if (!res.ok) {
    throw new Error(`/api/manifest 返回 ${res.status}`);
  }
  const manifest = (await res.json()) as Manifest;
  window.__manifest = manifest;
  return manifest;
}

/* ---------- Ruffle 按需加载 ---------- */
let ruffleLoading: Promise<boolean> | null = null;

/** 首次访问尽量等 SW 接管，使第一次播放器下载也能缓存；失败不阻塞播放。 */
function waitForCacheController(): Promise<void> {
  if (!("serviceWorker" in navigator) || navigator.serviceWorker.controller) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const finish = (): void => {
      clearTimeout(timer);
      navigator.serviceWorker.removeEventListener("controllerchange", finish);
      resolve();
    };
    const timer = setTimeout(finish, 1500);
    navigator.serviceWorker.addEventListener("controllerchange", finish);
    if (navigator.serviceWorker.controller) finish();
  });
}

/** 首次调用时注入 /ruffle/ruffle.js；之后复用同一 Promise。成功返回 true。 */
export function ensureRuffle(): Promise<boolean> {
  if (window.RufflePlayer) return Promise.resolve(true);
  if (!ruffleLoading) {
    ruffleLoading = waitForCacheController().then(() => new Promise<boolean>((resolve) => {
      const s = document.createElement("script");
      s.src = "/ruffle/ruffle.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.head.appendChild(s);
    }));
  }
  return ruffleLoading;
}

/* ---------- 页面骨架 ---------- */
/** 书页式双层导航：品牌及学科，当前位置及播放器操作。 */
export function topbar(
  crumb: Array<{ label: string; href?: string }>,
  right?: HTMLElement
): HTMLElement {
  const bar = el("header", "topbar");
  const row = el("div", "nav-row wrap");
  const brand = routeLink(PATHS.home, "nav-brand");
  brand.setAttribute("aria-label", "小朋友学习乐园，返回首页");
  brand.append(el("span", "nav-mark", "学"), el("span", "nav-brand-name", "小朋友学习乐园"));
  const subjects = el("nav", "nav-subjects");
  subjects.setAttribute("aria-label", "学科导航");
  for (const [label, href] of [["英语", PATHS.english], ["语文", PATHS.chinese], ["数学", PATHS.math]]) {
    const link = routeLink(href, "nav-subject");
    link.textContent = label;
    if (location.pathname === href || location.pathname.startsWith(href + "/")) {
      link.classList.add("is-active");
      link.setAttribute("aria-current", "true");
    }
    subjects.appendChild(link);
  }
  row.append(brand, subjects);
  bar.appendChild(row);
  if (crumb.length === 0 && !right) return bar;

  const trail = el("div", "nav-trail wrap");
  const crumbEl = el("nav", "crumb");
  crumbEl.setAttribute("aria-label", "路径导航");
  const home = routeLink(PATHS.home, "crumb-link");
  home.textContent = "首页";
  crumbEl.appendChild(home);
  crumb.forEach((seg) => {
    const sep = el("span", "sep", "/");
    sep.setAttribute("aria-hidden", "true");
    crumbEl.appendChild(sep);
    if (seg.href) {
      const a = routeLink(seg.href, "crumb-link");
      a.textContent = seg.label;
      crumbEl.appendChild(a);
    } else {
      const current = el("span", "crumb-now", seg.label);
      current.setAttribute("aria-current", "page");
      crumbEl.appendChild(current);
    }
  });
  trail.appendChild(crumbEl);
  if (right) trail.appendChild(right);
  bar.appendChild(trail);
  return bar;
}
